import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import neuralwardLogo from "../assets/neuralward.svg";

/**
 * Animated Original Logo
 * Uses the original SVG but applies the requested 3D flip/spring effect.
 */
const AnimatedOriginalLogo = ({ isHovered }) => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (isHovered) {
      setRotation(prev => prev + 360);
    }
  }, [isHovered]);

  return (
    <div className="relative flex items-center justify-center cursor-pointer perspective-1000 mr-2" style={{ minWidth: "32px", minHeight: "32px" }}>
      <motion.div
        className="relative preserve-3d"
        animate={{ 
          rotateY: rotation,
          scale: isHovered ? 1.1 : 1 
        }}
        transition={{ 
          rotateY: { duration: 0.96, ease: [0.76, 0, 0.24, 1] },
          scale: { duration: 0.3, ease: "easeOut" }
        }}
      >
        <img
          src={neuralwardLogo}
          alt=""
          width={40}
          height={40}
          className="h-10 w-10 drop-shadow-[0_0_15px_rgba(64,44,127,0.3)]"
          style={{ minWidth: "32px", minHeight: "32px" }}
          aria-hidden="true"
        />

        {/* Backside Glow (Visible during flip) */}
        <div 
          className="absolute inset-0 bg-[#402c7f]/10 rounded-full blur-2xl backface-hidden"
          style={{ transform: "rotateY(180deg)" }}
        />
      </motion.div>
    </div>
  );
};

const KANA_POOL = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";

const LetterSlot = ({ char, isHovered, index, totalLength }) => {
  const [displayChar, setDisplayChar] = useState(char);
  
  useEffect(() => {
    if (isHovered) {
      let counter = 0;
      // Accelerated left-to-right finish using an ease-out curve
      const t = index / Math.max(1, totalLength - 1);
      const maxCycles = Math.round(5 + 27 * (1 - Math.pow(1 - t, 1.5))); 
      
      const interval = setInterval(() => {
        counter++;
        if (counter >= maxCycles) {
          setDisplayChar(char);
          clearInterval(interval);
        } else {
          setDisplayChar(KANA_POOL[Math.floor(Math.random() * KANA_POOL.length)]);
        }
      }, 30);
      
      return () => clearInterval(interval);
    } else {
      setDisplayChar(char);
    }
  }, [isHovered, char, index, totalLength]);

  return (
    <span className="relative inline-flex items-center justify-center">
      {/* Invisible target character to set the exact natural width and maintain kerning */}
      <span className="invisible">{char}</span>
      {/* Absolutely positioned scrambling character that doesn't affect layout */}
      <span 
        className="absolute"
        style={{ color: displayChar !== char ? "#ee2a7b" : undefined }}
      >
        {displayChar}
      </span>
    </span>
  );
};

/**
 * KANA SCRAMBLE TEXT
 * Scrambles characters using Katakana before locking onto the target character left-to-right.
 */
const CrypticTwistText = ({ text, isHovered }) => {
  const letters = text.split("");

  return (
    <div className="flex animate-brand-text">
      {letters.map((char, i) => (
        <LetterSlot key={i} char={char} isHovered={isHovered} index={i} totalLength={letters.length} />
      ))}
    </div>
  );
};

const BrandLogo = ({ isHovered }) => {
  return (
    <>
      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .perspective-500 { perspective: 500px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        
        @keyframes slideInBrandText {
          0% { opacity: 0; transform: translateX(-20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-brand-text {
          animation: slideInBrandText 0.5s ease-out 0.2s both;
        }
      `}</style>
      <AnimatedOriginalLogo isHovered={isHovered} />
      <CrypticTwistText text="neuralward" isHovered={isHovered} />
    </>
  );
};

export default BrandLogo;
